import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Truck, User, Car, FileText, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AgreementBlock from "@/components/AgreementBlock";
import { DriverAgreementContent } from "@/lib/agreements";
import { API } from "@/lib/api";

export default function DriverRegister() {
  const navigate = useNavigate();
  const { registerDriver } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    drivers_license: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_color: "",
    license_plate: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
        toast.error("Please fill in all personal info fields");
        return false;
      }
      if (!formData.password || formData.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        return false;
      }
    } else if (step === 2) {
      if (!formData.address || !formData.city || !formData.state || !formData.zip_code) {
        toast.error("Please fill in all address fields");
        return false;
      }
    } else if (step === 3) {
      if (!formData.drivers_license || !formData.vehicle_make || !formData.vehicle_model || !formData.license_plate) {
        toast.error("Please fill in all vehicle info fields");
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    // Vehicle year guardrail — must be 2016 or newer
    const yearNum = parseInt(formData.vehicle_year, 10);
    if (!yearNum || yearNum < 2016) {
      toast.error("Vehicle must be 2016 or newer to qualify as a Link driver.");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Please accept the driver agreement, terms, and privacy policy to continue");
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...data } = formData;
      const user = await registerDriver(data);
      // Record legal acceptance — best-effort, never blocks account creation
      try {
        await axios.post(`${API}/legal/accept`, {
          doc_ids: ["driver-agreement", "terms", "privacy", "aup"],
          signer_name: `${formData.first_name} ${formData.last_name}`.trim(),
          signer_email: formData.email,
          signer_role: "driver",
          context: "driver-register",
        });
      } catch (_) { /* non-fatal */ }
      toast.success("Driver account created! Upload your documents to complete registration.");
      navigate("/driver/profile");
    } catch (err) {
      console.error("Registration error:", err);
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-5">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => step > 1 ? setStep(prev => prev - 1) : navigate(-1)}
            className="text-zinc-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Become a Driver</h1>
            <p className="text-sm text-zinc-500">Step {step} of 3</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3].map(i => (
            <div 
              key={i} 
              className={`flex-1 h-2 rounded-full ${step >= i ? 'bg-[#0047FF]' : 'bg-zinc-800'}`} 
            />
          ))}
        </div>

        {/* Eligibility requirements — shown on every step so drivers know what's needed */}
        <Card className="bg-[#121212] border-[#0047FF]/30 mb-4" data-testid="driver-requirements-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[#4D88FF]" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-bold">To Drive for Link Ride</p>
            </div>
            <p className="text-xs text-white/60 mb-3">Drivers must provide the following:</p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2 text-white/85">
                <span className="text-[#4D88FF] font-bold">•</span>
                <span>Valid driver's license</span>
              </li>
              <li className="flex gap-2 text-white/85">
                <span className="text-[#4D88FF] font-bold">•</span>
                <div>
                  <span>Proof of insurance</span>
                  <ul className="mt-1 ml-3 space-y-1 text-xs text-white/60">
                    <li>– For passengers:{" "}
                      <span className="text-[#00E676]">full coverage</span> + rideshare endorsement{" "}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label="What counts as full coverage"
                            className="inline-flex items-center align-middle text-white/50 hover:text-white"
                            data-testid="full-coverage-info"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="top"
                          className="w-72 bg-[#0a0a0a] border border-white/10 text-xs text-white/85 leading-relaxed"
                          data-testid="full-coverage-popover"
                        >
                          <p className="font-bold text-white mb-2">What counts as "full coverage"</p>
                          <ul className="space-y-1 list-disc list-inside">
                            <li><b>Liability</b> (bodily injury + property damage)</li>
                            <li><b>Collision</b> (damage to your vehicle in a crash)</li>
                            <li><b>Comprehensive</b> (theft, vandalism, weather)</li>
                            <li><b>Rideshare endorsement</b> — required for passenger trips</li>
                          </ul>
                          <p className="mt-2 text-white/55">
                            Send your declarations page (the one-pager from your insurer listing
                            these line items) when you apply.
                          </p>
                        </PopoverContent>
                      </Popover>
                    </li>
                    <li>– For deliveries: standard auto policy is OK</li>
                  </ul>
                </div>
              </li>
              <li className="flex gap-2 text-white/85">
                <span className="text-[#4D88FF] font-bold">•</span>
                <span>Vehicle registration</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Step 1: Personal Info */}
        {step === 1 && (
          <Card className="bg-[#121212] border-white/5" data-testid="step-1-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#0047FF]/10">
                  <User className="w-5 h-5 text-[#4D88FF]" />
                </div>
                <div>
                  <CardTitle className="text-lg text-white">Personal Information</CardTitle>
                  <CardDescription className="text-zinc-500">Tell us about yourself</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => updateField("first_name", e.target.value)}
                    placeholder="John"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="first-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => updateField("last_name", e.target.value)}
                    placeholder="Doe"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="last-name"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="john@example.com"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="email"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  placeholder="555-123-4567"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="phone"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateField("password", e.target.value)}
                  placeholder="Min 6 characters"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="password"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Confirm Password *</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateField("confirmPassword", e.target.value)}
                  placeholder="Confirm password"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="confirm-password"
                />
              </div>
              <Button 
                onClick={handleNext}
                className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                data-testid="next-step-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Address */}
        {step === 2 && (
          <Card className="bg-[#121212] border-white/5" data-testid="step-2-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#0047FF]/10">
                  <FileText className="w-5 h-5 text-[#4D88FF]" />
                </div>
                <div>
                  <CardTitle className="text-lg text-white">Address Information</CardTitle>
                  <CardDescription className="text-zinc-500">Where do you live?</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400">Street Address *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  placeholder="123 Main Street"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="address"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400">City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    placeholder="Austin"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="city"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">State *</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => updateField("state", e.target.value)}
                    placeholder="TX"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="state"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">ZIP Code *</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => updateField("zip_code", e.target.value)}
                  placeholder="78701"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="zip-code"
                />
              </div>
              <Button 
                onClick={handleNext}
                className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                data-testid="next-step-2"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Vehicle Info */}
        {step === 3 && (
          <Card className="bg-[#121212] border-white/5" data-testid="step-3-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#0047FF]/10">
                  <Car className="w-5 h-5 text-[#4D88FF]" />
                </div>
                <div>
                  <CardTitle className="text-lg text-white">Vehicle Information</CardTitle>
                  <CardDescription className="text-zinc-500">Tell us about your vehicle</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400">Driver's License Number *</Label>
                <Input
                  value={formData.drivers_license}
                  onChange={(e) => updateField("drivers_license", e.target.value)}
                  placeholder="DL123456789"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="drivers-license"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400">Vehicle Make *</Label>
                  <Input
                    value={formData.vehicle_make}
                    onChange={(e) => updateField("vehicle_make", e.target.value)}
                    placeholder="Toyota"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="vehicle-make"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Vehicle Model *</Label>
                  <Input
                    value={formData.vehicle_model}
                    onChange={(e) => updateField("vehicle_model", e.target.value)}
                    placeholder="Camry"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="vehicle-model"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400">Year</Label>
                  <Input
                    type="number"
                    min="2016"
                    max="2027"
                    value={formData.vehicle_year}
                    onChange={(e) => updateField("vehicle_year", e.target.value)}
                    placeholder="2022"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="vehicle-year"
                  />
                  <p className="text-[10px] text-amber-400/80 mt-1">
                    Vehicle must be <strong>2016 or newer</strong>.
                  </p>
                </div>
                <div>
                  <Label className="text-zinc-400">Color</Label>
                  <Input
                    value={formData.vehicle_color}
                    onChange={(e) => updateField("vehicle_color", e.target.value)}
                    placeholder="Blue"
                    className="bg-zinc-800 border-white/10 text-white mt-1"
                    data-testid="vehicle-color"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">License Plate *</Label>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => updateField("license_plate", e.target.value)}
                  placeholder="ABC-1234"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="license-plate"
                />
              </div>
              <label className="flex items-start gap-2 mt-2 cursor-pointer" data-testid="driver-accept-terms">
                <Checkbox
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(!!v)}
                  className="mt-0.5"
                  data-testid="driver-accept-terms-checkbox"
                />
                <span className="text-xs text-zinc-400 leading-relaxed">
                  I agree to the{" "}
                  <Link to="/legal/driver-agreement" target="_blank" className="text-[#4D88FF] hover:underline">Driver Agreement</Link>,{" "}
                  <Link to="/legal/terms" target="_blank" className="text-[#4D88FF] hover:underline">Terms of Service</Link>,{" "}
                  <Link to="/legal/aup" target="_blank" className="text-[#4D88FF] hover:underline">Acceptable Use Policy</Link>, and{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-[#4D88FF] hover:underline">Privacy Policy</Link>.
                </span>
              </label>
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0 font-bold uppercase tracking-wide"
                data-testid="submit-registration"
              >
                {loading ? "Creating Account..." : "Complete Registration"}
              </Button>
              <p className="text-xs text-zinc-500 text-center">
                You'll need to upload your license and insurance documents after registration.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-zinc-500">
            Already have an account?{" "}
            <Button 
              variant="link" 
              className="text-[#4D88FF] p-0" 
              onClick={() => navigate("/login?redirect=/driver")}
              data-testid="login-link"
            >
              Sign in
            </Button>
          </p>
        </div>
      </div>
    </div>
  );
}
